import React, { forwardRef } from 'react';
import { ComputedPlacement } from './placement';

type Props = {
  title?: string;
  description?: string;
  stepIndex: number;
  totalSteps: number;
  showProgress: boolean;
  isFirst: boolean;
  isLast: boolean;
  placement: ComputedPlacement | null;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
};

const TourPopover = forwardRef<HTMLDivElement, Props>(
  (
    {
      title,
      description,
      stepIndex,
      totalSteps,
      showProgress,
      isFirst,
      isLast,
      placement,
      onNext,
      onPrev,
      onClose,
    },
    ref,
  ) => {
    const isDark = document.documentElement.classList.contains('dark');

    const bg = isDark ? '#2a2a2a' : '#ffffff';
    const border = isDark ? '#444444' : '#e5e7eb';
    const textPrimary = isDark ? '#f9fafb' : '#111827';
    const textSecondary = isDark ? '#9ca3af' : '#6b7280';
    const btnBg = isDark ? '#3f3f3f' : '#f3f4f6';
    const btnBgHover = isDark ? '#525252' : '#e5e7eb';
    const accentBg = '#5b5ce2';

    return (
      <div
        ref={ref}
        style={{
          position: 'fixed',
          top: placement ? placement.top : 0,
          left: placement ? placement.left : 0,
          visibility: placement ? 'visible' : 'hidden',
          zIndex: 999999,
          width: 420,
          backgroundColor: bg,
          border: `1px solid ${border}`,
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          padding: '20px 22px 18px',
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <span
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: textPrimary,
              lineHeight: 1.4,
              flex: 1,
              paddingRight: 8,
            }}
          >
            {title}
          </span>
          <button
            type="button"
            aria-label="Close tour"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 20,
              lineHeight: 1,
              color: textSecondary,
              padding: '0 2px',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        {description && (
          <div
            style={{ fontSize: 14, color: textSecondary, lineHeight: 1.6, marginBottom: 16 }}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {showProgress ? (
            <span style={{ fontSize: 12, color: textSecondary }}>
              {stepIndex + 1} / {totalSteps}
            </span>
          ) : (
            <span />
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            {!isFirst && (
              <button
                type="button"
                onClick={onPrev}
                style={{
                  fontSize: 13,
                  padding: '6px 14px',
                  borderRadius: 6,
                  border: `1px solid ${border}`,
                  background: btnBg,
                  color: textPrimary,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLButtonElement).style.background = btnBgHover)
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLButtonElement).style.background = btnBg)
                }
              >
                ← Prev
              </button>
            )}
            <button
              type="button"
              onClick={onNext}
              style={{
                fontSize: 13,
                padding: '6px 14px',
                borderRadius: 6,
                border: 'none',
                background: accentBg,
                color: '#ffffff',
                cursor: 'pointer',
                fontWeight: 600,
                opacity: 1,
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = '0.85')}
              onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = '1')}
            >
              {isLast ? 'Done' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

TourPopover.displayName = 'TourPopover';
export default TourPopover;
